import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Download } from "lucide-react";
import { Input } from "@/components/ui/input";
import { PageShell } from "@/components/site/Site";
import { api, errMsg, SUPPORT_EMAIL } from "@/lib/api";

const LABEL = {
  payment_status: { awaiting_confirmation: "Awaiting PayPal confirmation", paid: "Paid", initiated: "Awaiting payment", not_started: "Awaiting payment", cancelled: "Payment cancelled", failed: "Payment failed", refunded: "Refunded" },
  report_status: { ready: "Being prepared with care", generating: "Being prepared with care", awaiting_ruleset: "Being prepared with care", not_generated: "Not started", failed: "Delayed — our team has been notified", withdrawn: "Withdrawn (refunded)" },
  email_status: { sent: "Emailed", sending: "Sending", scheduled: "Usually within 24 hours of your order", failed: "Delivery problem — our team has been notified", not_sent: "Usually within 24 hours of your order", withdrawn: "—" },
};

const Row = ({ k, v, testId }) => (
  <div className="flex justify-between border-t border-[#E6DEC8] py-3 text-sm"><span className="text-stone-500">{k}</span><span className="font-medium" data-testid={testId}>{v}</span></div>
);

export default function Receipt() {
  const [params] = useSearchParams();
  const [ref, setRef] = useState(params.get("ref") || "");
  const [email, setEmail] = useState(params.get("email") || "");
  const [o, setO] = useState(null);
  const [err, setErr] = useState("");
  const look = useCallback(async () => {
    setErr("");
    try { setO((await api.get(`/orders/${encodeURIComponent(ref.trim())}/receipt`, { params: { email: email.trim() } })).data); } catch (e) { setO(null); setErr(errMsg(e)); }
  }, [ref, email]);
  useEffect(() => { if (params.get("ref") && params.get("email")) look(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <PageShell>
      <section className="mx-auto max-w-2xl px-5 py-20 sm:px-8">
        <p className="font-mono-ui text-xs uppercase tracking-[0.3em] text-[#8B5A2B]">Your order</p>
        <h1 className="mt-4 font-serif-display text-4xl sm:text-5xl">Order & Receipt</h1>
        <form onSubmit={(e) => { e.preventDefault(); look(); }} className="mt-10 grid gap-3 sm:grid-cols-[1fr_1fr_auto]" data-testid="receipt-lookup-form">
          <Input placeholder="Order reference (NAM-…)" value={ref} onChange={(e) => setRef(e.target.value)} className="h-12 bg-white" data-testid="receipt-ref-input" required />
          <Input type="email" placeholder="Email used for the order" value={email} onChange={(e) => setEmail(e.target.value)} className="h-12 bg-white" data-testid="receipt-email-input" required />
          <button className="h-12 rounded-full bg-stone-900 px-6 text-sm text-white hover:bg-[#5B2C36]" data-testid="receipt-lookup-btn">Find</button>
        </form>
        {err && <p className="mt-4 text-sm text-[#5B2C36]" data-testid="receipt-error">{err}</p>}
        {o && (
          <div className="fade-up mt-10 border border-stone-900 bg-white p-8" data-testid="receipt-card">
            <p className="font-serif-display text-3xl" data-testid="receipt-name">{o.display_name}</p>
            <p className="mt-1 font-mono-ui text-xs text-stone-500">{o.ref}</p>
            <div className="mt-6">
              <Row k="Order date" v={(o.created_at || "").slice(0, 10)} />
              {o.discount > 0 && <Row k={`Discount (${o.promo_code})`} v={`− ${o.currency} ${Number(o.discount).toFixed(2)}`} />}
              <Row k="Amount" v={`${o.currency} ${Number(o.amount).toFixed(2)}`} testId="receipt-amount" />
              <Row k="Payment" v={LABEL.payment_status[o.payment_status] || o.payment_status} testId="receipt-payment-status" />
              {o.paid_at && <Row k="Paid on" v={o.paid_at.slice(0, 10)} />}
              <Row k="Report" v={o.email_status === "sent" && o.report_status === "ready" ? "Ready" : LABEL.report_status[o.report_status] || o.report_status} testId="receipt-report-status" />
              {o.gift && <Row k="Gift for" v={`${o.display_name} · ${o.gift.recipient_email}`} testId="receipt-gift" />}
              <Row k="Email delivery" v={o.paypal_me && ["scheduled", "not_sent"].includes(o.email_status) ? "Usually within 24 hours of your payment being confirmed" : LABEL.email_status[o.email_status] || o.email_status} testId="receipt-email-status" />
            </div>
            {o.download_url && (
              <a href={o.download_url} className="mt-8 inline-flex items-center gap-2 rounded-full bg-stone-900 px-6 py-3 text-sm text-white hover:bg-[#5B2C36]" data-testid="receipt-download-btn">
                <Download className="h-4 w-4" /> Download your report (PDF)
              </a>
            )}
            <p className="mt-6 text-xs text-stone-500">Download links are personal and expire after 14 days; revisit this page for a fresh link. Questions: {SUPPORT_EMAIL}</p>
          </div>
        )}
      </section>
    </PageShell>
  );
}
